import { EllipsisVerticalIcon, PlayPauseIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useDebounce } from '@superfunnel/react-hooks/useDebounce';
import { usePrevious } from '@superfunnel/react-hooks/usePrevious';
import { AssetData } from '@superfunnel/types/asset-data';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/DropdownMenu';
import Input from '@superfunnel/ui-core/Input';
import Modal from '@superfunnel/ui-core/Modal';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/Tabs';
import { distinct } from '@superfunnel/utils/array';

import { FileDropZone } from '../../components/FileDropZone';
import { GalleryCardFrame } from '../../components/Gallery/GalleryCardFrame';
import { GallerySearchInput } from '../../components/Gallery/GallerySearchInput';
import { GalleryGrid } from '../../components/Gallery/GalleryGrid';
import { useSnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  deleteAssetFromLibrary,
  fetchAssets,
  markUsed,
  updateAsset,
} from '../../services/ImagesService';
import { assetUrlToCDNUrl } from '../../utils/assets';
import {
  handleUpload,
  ImageLibraryAssetData,
  UploadedAssetData,
} from '../../utils/file-upload';

type AssetSource = 'Library' | 'Unsplash' | 'Pexels' | 'Upload';

const ASSET_SOURCES: AssetSource[] = [
  'Library',
  'Unsplash',
  'Pexels',
  'Upload',
];

interface Props {
  multi?: boolean;
  open: boolean;
  types?: ('image' | 'video')[];
  onClose: () => void;
  onSelected: (images: ImageLibraryAssetData[]) => void;
}

const pageSize = 20;

export default function ImageLibraryModal({
  multi,
  open,
  types = ['image'],
  onClose,
  onSelected,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<AssetSource>('Library');
  const prevActiveTab = usePrevious(activeTab);
  const [selectedImages, setSelectedImages] = useState<AssetData[]>([]);

  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const [images, setImages] = useState<AssetData[] | null>(null);

  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState<{
    selectedImage: AssetData;
  }>();
  const [renameImageModalOpen, setRenameImageModalOpen] = useState<{
    selectedImage: AssetData;
    newName?: string;
  }>();

  useEffect(() => {
    if (!open) {
      setSelectedImages([]);
      setActiveTab('Library');
      setSearch('');
      setImages(null);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (!open) return;

    if (activeTab !== prevActiveTab) {
      setImages(null);
      setSelectedImages([]);
    }

    let ok = true;

    setLoadingNextPage(true);

    fetchAssets(
      selectedAccount,
      activeTab,
      debouncedSearch,
      undefined,
      pageSize,
      types,
    )
      .then((data) => {
        if (!ok) return;
        setImages(data);
        setHasMore(data.length === pageSize);
      })
      .catch((e) => {
        Sentry.captureException(e);
        console.error('Error fetching images:', e);
      })
      .finally(() => {
        setLoadingNextPage(false);
      });

    return () => {
      ok = false;
    };
  }, [debouncedSearch, open, activeTab, prevActiveTab, selectedAccount, types]);

  const nextPage = useCallback(() => {
    if (!selectedAccount) return;
    if (!images) return;

    setLoadingNextPage(true);

    const page = images.length / pageSize + 1;
    fetchAssets(selectedAccount, activeTab, debouncedSearch, page, pageSize)
      .then((data) => {
        setImages((existing) => [...(existing || []), ...data]);
        setHasMore(data.length === pageSize);
      })
      .catch((error) => {
        console.error('Error fetching images:', error);
        Sentry.captureException(error);
      })
      .finally(() => {
        setLoadingNextPage(false);
      });
  }, [selectedAccount, images, activeTab, debouncedSearch]);

  const toggleImage = (image: AssetData) => {
    if (!multi) {
      setSelectedImages([image]);
      return;
    }

    setSelectedImages((current) =>
      current.some((s) => s.id === image.id)
        ? current.filter((s) => s.id !== image.id)
        : distinct([...current, image], (a) => a.id),
    );
  };

  const handleInsert = () => {
    for (const selectedImage of selectedImages) {
      if (
        'downloadLocation' in selectedImage &&
        selectedImage.downloadLocation &&
        typeof selectedImage.downloadLocation === 'string'
      ) {
        markUsed('unsplash', selectedImage.downloadLocation);
        delete selectedImage.downloadLocation;
      }
    }

    onSelected(selectedImages);
  };

  return (
    <Modal
      width="6xl"
      height="fixed"
      actionsAlignment="end"
      open={open}
      onClose={onClose}
      title="Asset Library"
      actions={[
        { type: 'secondary', title: 'Cancel', onClick: onClose },
        {
          type: 'primary',
          title: `Insert${multi && selectedImages.length > 1 ? ` (${selectedImages.length})` : ''}`,
          disabled: !selectedImages.length,
          onClick: handleInsert,
        },
      ]}
      toolbar={
        <>
          {activeTab !== 'Upload' && (
            <GallerySearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search assets..."
            />
          )}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as AssetSource)}
            className="lg:ml-auto"
          >
            <TabsList size="md" className="w-full lg:w-fit">
              {ASSET_SOURCES.map((source) => (
                <TabsTrigger
                  key={source}
                  value={source}
                  size="md"
                  className="lg:flex-none"
                >
                  {source}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </>
      }
      bodyRef={setScrollElement}
    >
      <AlertModal
        type="warn"
        title={`Are you sure you want to delete ${deleteImageModalOpen?.selectedImage?.alt || 'this image'}?`}
        description="This will not affect pages that are already using this image. This action cannot be undone."
        open={!!deleteImageModalOpen}
        onClose={() => setDeleteImageModalOpen(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setDeleteImageModalOpen(undefined),
          },
          {
            type: 'primary',
            title: 'Delete',
            // color: 'negative',
            onClick: () => {
              if (!selectedAccount) return;
              const selectedImage = deleteImageModalOpen?.selectedImage;
              if (!selectedImage) return;

              const oldPosition = images?.findIndex(
                (i) => i.id === selectedImage.id,
              );
              setImages(
                (images) =>
                  images?.filter((i) => i.id !== selectedImage.id) || null,
              );

              deleteAssetFromLibrary(selectedAccount, selectedImage.id).catch(
                (e) => {
                  console.error(e);
                  Sentry.captureException(e);

                  setImages((images) => {
                    if (oldPosition !== undefined && images) {
                      const newImages = [...images];
                      newImages.splice(oldPosition, 0, selectedImage);
                      return newImages;
                    }
                    return images;
                  });
                },
              );

              setDeleteImageModalOpen(undefined);
            },
          },
        ]}
      />

      <AlertModal
        type="info"
        title="Rename image"
        open={!!renameImageModalOpen}
        onClose={() => setRenameImageModalOpen(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setRenameImageModalOpen(undefined),
          },
          {
            type: 'primary',
            title: 'Save',
            // color: 'primary',
            onClick: () => {
              if (!selectedAccount) return;
              const selectedImage = renameImageModalOpen?.selectedImage;
              const newName = renameImageModalOpen?.newName;
              if (!selectedImage || !newName || selectedImage.alt === newName) {
                setRenameImageModalOpen(undefined);
                return;
              }

              const oldName = selectedImage.alt;
              setImages(
                (images) =>
                  images?.map((i) =>
                    i.id === selectedImage.id ? { ...i, alt: newName } : i,
                  ) || null,
              );

              updateAsset(selectedAccount, selectedImage.id, {
                alt: newName,
              }).catch((e) => {
                console.error(e);
                Sentry.captureException(e);

                setImages(
                  (images) =>
                    images?.map((i) =>
                      i.id === selectedImage.id
                        ? { ...i, alt: oldName || '' }
                        : i,
                    ) || null,
                );
              });

              setRenameImageModalOpen(undefined);
            },
          },
        ]}
      >
        <Input
          value={renameImageModalOpen?.newName || ''}
          onChange={(e) =>
            setRenameImageModalOpen((s) =>
              s
                ? {
                    ...s,
                    newName: e.target.value,
                  }
                : undefined,
            )
          }
          // fullWidth
        />
      </AlertModal>

      {activeTab === 'Upload' ? (
        <Upload
          onUploaded={(selected) => {
            if (multi || selected.length === 1) {
              onSelected(selected);
            } else {
              setActiveTab('Library');
            }
          }}
          types={types}
        />
      ) : (
        <GalleryGrid
          scrollElement={scrollElement}
          isLoading={images == null}
          emptyMessage="No assets found"
          hasNextPage={hasMore}
          isFetchingNextPage={loadingNextPage}
          onLoadMore={nextPage}
          estimatedItemHeight={220}
          maxLanes={4}
        >
          {images?.map((image) => (
            <AssetCard
              key={image.id}
              image={image}
              source={activeTab}
              isSelected={selectedImages.some((s) => s.id === image.id)}
              onToggle={() => toggleImage(image)}
              onRename={() =>
                setRenameImageModalOpen({
                  selectedImage: image,
                  newName: image.alt,
                })
              }
              onDelete={() => setDeleteImageModalOpen({ selectedImage: image })}
            />
          ))}
        </GalleryGrid>
      )}
    </Modal>
  );
}

const AssetMedia = ({ image }: { image: AssetData }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  if (image.type === 'video' && image.source === 'vimeo') {
    return <iframe src={image.url} className="aspect-video w-full" />;
  }

  if (image.type === 'video') {
    return (
      <div className="relative">
        <video
          ref={videoRef}
          src={assetUrlToCDNUrl(image.url)}
          preload="metadata"
          controls={false}
          className="disable-user-select pointer-events-none block w-full"
        />
        <Button
          size="icon"
          color="positive"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          aria-label="Play or pause video"
          onClick={(event) => {
            event.stopPropagation();
            const video = videoRef.current;
            if (!video) return;
            if (video.paused) video.play();
            else video.pause();
          }}
        >
          <PlayPauseIcon className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <img
      src={assetUrlToCDNUrl(image.thumbnail || image.url)}
      alt={image.alt}
      width={image.width}
      height={image.height}
      className="disable-user-select pointer-events-none block h-auto w-full"
    />
  );
};

const AttributionLink = ({ href, label }: { href?: string; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    onClick={(event) => event.stopPropagation()}
    className="text-brand-600 underline"
  >
    {label}
  </a>
);

const AssetCard = ({
  image,
  source,
  isSelected,
  onToggle,
  onRename,
  onDelete,
}: {
  image: AssetData;
  source: AssetSource;
  isSelected: boolean;
  onToggle: () => void;
  onRename: () => void;
  onDelete: () => void;
}) => (
  <GalleryCardFrame
    title={image.alt || 'asset'}
    isSelected={isSelected}
    onToggle={onToggle}
  >
    <div className="relative w-full overflow-hidden rounded-lg bg-base-100">
      <AssetMedia image={image} />
    </div>

    <div className="flex min-h-8 min-w-0 items-center gap-1 py-1 pr-1 pl-1.5">
      {source === 'Library' ? (
        <>
          <p className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
            {image.alt || 'Untitled'}
          </p>
          <div onClick={(event) => event.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="size-6 rounded-full"
                  aria-label={`Open actions for ${image.alt || 'asset'}`}
                >
                  <EllipsisVerticalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onRename}>Rename</DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={onDelete}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      ) : (
        <p className="min-w-0 flex-1 truncate text-xs text-base-600">
          Photo by{' '}
          <AttributionLink
            href={image.photographerUrl}
            label={image.photographer || source}
          />{' '}
          on{' '}
          <AttributionLink
            href={`https://${source.toLowerCase()}.com`}
            label={source}
          />
        </p>
      )}
    </div>
  </GalleryCardFrame>
);

const UploadProgressBar = ({ progress }: { progress: number }) => {
  return (
    <div className="w-full rounded-full bg-base-200">
      <div
        className="min-w-5 rounded-full bg-brand-500 p-0.5 text-center text-xs leading-none font-medium text-white transition-[width]"
        style={{ width: progress + '%' }}
      >
        {progress}%
      </div>
    </div>
  );
};

const Upload = ({
  onUploaded,
  types,
}: {
  onUploaded: (images: UploadedAssetData[]) => void;
  types: ('image' | 'video')[];
}) => {
  const { selectedAccount } = useContext(UserContext);
  const { showError } = useSnackbarContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const uploadFiles = (selectedFiles: File[]) => {
    if (!selectedAccount || !selectedFiles.length) return;

    setUploadProgress(0);
    handleUpload(selectedAccount.id, selectedFiles, (progress) =>
      setUploadProgress(Math.ceil(progress)),
    )
      .then((res) => {
        setUploadProgress(null);
        onUploaded(res);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setUploadProgress(null);
        console.error(e);
        showError(e);
      });
  };

  if (uploadProgress != null) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <UploadProgressBar progress={uploadProgress} />
      </div>
    );
  }

  return (
    <div className="h-full p-1">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={types
          .map((t) => (t === 'image' ? 'image/*' : 'video/*'))
          .join(',')}
        className="hidden"
        onChange={(event) => {
          uploadFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      <FileDropZone
        hint={`Upload ${types.map((t) => `${t}s`).join(' or ')}`}
        onBrowse={() => inputRef.current?.click()}
        onDropFiles={uploadFiles}
        className="h-full min-h-72"
      />
    </div>
  );
};
